import React, { useState, useEffect } from 'react';
// eslint-disable-next-line no-unused-vars
import DatePicker, { registerLocale } from 'react-datepicker';
import fr from 'date-fns/locale/fr';
import { createOrder, getPastries } from '../services/api';
import 'react-datepicker/dist/react-datepicker.css';
import './OrderForm.css';

registerLocale('fr', fr);

const LOCATIONS = ['Arvillard', 'Barraux'];

function OrderForm({ onOrderCreated }) {
  const [customerName, setCustomerName] = useState('');
  const [pickupLocation, setPickupLocation] = useState(LOCATIONS[0]);
  const [pickupDate, setPickupDate] = useState(null);
  const [pickupTime, setPickupTime] = useState('10:00');
  const [orderComment, setOrderComment] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingOrderData, setPendingOrderData] = useState(null);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    setLoadingProducts(true);
    const result = await getPastries();
    if (result.success && result.pastries) {
      setProducts(result.pastries);
    }
    setLoadingProducts(false);
  };

  const addItem = (product) => {
    const existingItem = items.find(item => item.id === product.id);
    if (existingItem) {
      setItems(items.map(item =>
        item.id === product.id
          ? { ...item, quantity: item.quantity }
          : item
      ));
    } else {
      setItems([...items, { ...product, quantity: 4 }]);
    }
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      setItems(items.filter(item => item.id !== productId));
    } else {
      setItems(items.map(item =>
        item.id === productId
          ? { ...item, quantity }
          : item
      ));
    }
  };

  const removeItem = (productId) => {
    setItems(items.filter(item => item.id !== productId));
  };

  const calculateTotal = () => {
    return items.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (items.length === 0) {
      alert('Veuillez ajouter au moins un article à la commande');
      return;
    }

    const pickupDateString = pickupDate ? pickupDate.toISOString().split('T')[0] : '';

    const orderData = {
      customerName,
      pickupLocation,
      pickupDate: pickupDateString,
      pickupTime,
      orderComment,
      items: items.map(item => ({
        name: item.name,
        quantity: item.quantity,
        price: item.price
      })),
      total: calculateTotal()
    };

    setPendingOrderData(orderData);
    setShowConfirmModal(true);
  };

  const handleConfirmOrder = async () => {
    if (!pendingOrderData) return;

    setLoading(true);
    const result = await createOrder(pendingOrderData);
    setLoading(false);

    if (result.success) {
      setShowConfirmModal(false);
      setPendingOrderData(null);
      setCustomerName('');
      setPickupDate(null);
      setPickupTime('10:00');
      setPickupLocation(LOCATIONS[0]);
      setOrderComment('');
      setItems([]);
      onOrderCreated();
    } else {
      alert(result.message || 'Erreur lors de la création de la commande');
    }
  };

  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  return (
    <div className="order-form-container">
      <h2>Nouvelle commande</h2>

      <form onSubmit={handleSubmit} className="order-form">
        <div className="form-section">
          <h3>Informations client</h3>
          <div className="form-group">
            <label htmlFor="customerName">Nom du client *</label>
            <input
              type="text"
              id="customerName"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="Nom et prénom"
              required
            />
          </div>
        </div>

        <div className="form-section">
          <h3>Informations de retrait</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="pickupLocation">Lieu de retrait *</label>
              <select
                id="pickupLocation"
                value={pickupLocation}
                onChange={(e) => setPickupLocation(e.target.value)}
                required
              >
                {LOCATIONS.map(location => (
                  <option key={location} value={location}>{location}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="pickupDate">Date de retrait *</label>
              <DatePicker
                selected={pickupDate}
                onChange={(date) => setPickupDate(date)}
                minDate={new Date(getTomorrowDate())}
                dateFormat="dd/MM/yyyy"
                locale="fr"
                placeholderText="Sélectionner une date"
                required
                className="datepicker-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor="pickupTime">Heure de retrait *</label>
              <input
                type="time"
                id="pickupTime"
                value={pickupTime}
                onChange={(e) => setPickupTime(e.target.value)}
                required
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Articles</h3>
          {loadingProducts ? (
            <p className="loading-message">Chargement des articles...</p>
          ) : (
            <div className="products-grid">
              {products.length > 0 ? (
                products.map(product => (
                  <button
                    key={product.id}
                    type="button"
                    className="product-button"
                    onClick={() => addItem(product)}
                  >
                    <span>{product.name}</span>
                  </button>
                ))
              ) : (
                <p className="no-products">Aucun article disponible</p>
              )}
            </div>
          )}

          {items.length > 0 && (
            <div className="cart">
              <h4>Commande</h4>
              <ul className="cart-items">
                {items.map(item => (
                  <li key={item.id} className="cart-item">
                    <span className="item-name">{item.name}</span>
                    <div className="item-controls">
                      <select
                        value={item.quantity}
                        onChange={(e) => updateQuantity(item.id, parseInt(e.target.value) || 0)}
                        className="quantity-select"
                      >
                        {[4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24].map(qty => (
                          <option key={qty} value={qty}>{qty} personne{qty > 1 ? 's' : ''}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => removeItem(item.id)}
                        className="remove-button"
                      >
                        Supprimer
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="form-section">
          <h3>Commentaire</h3>
          <div className="form-group">
            <label htmlFor="orderComment">Commentaire (optionnel)</label>
            <textarea
              id="orderComment"
              value={orderComment}
              onChange={(e) => setOrderComment(e.target.value)}
              placeholder="Instructions spéciales, allergies, personnalisation..."
              rows="3"
            />
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="submit-button" disabled={loading || items.length === 0}>
            {loading ? 'Création...' : 'Créer la commande'}
          </button>
        </div>
      </form>

      {showConfirmModal && pendingOrderData && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Confirmer la commande</h2>

            <div className="modal-section">
              <h3>Détails du client</h3>
              <div className="modal-detail-row">
                <span className="modal-label">Nom:</span>
                <span className="modal-value">{pendingOrderData.customerName}</span>
              </div>
            </div>

            <div className="modal-section">
              <h3>Informations de retrait</h3>
              <div className="modal-detail-row">
                <span className="modal-label">Lieu:</span>
                <span className="modal-value">{pendingOrderData.pickupLocation}</span>
              </div>
              <div className="modal-detail-row">
                <span className="modal-label">Date:</span>
                <span className="modal-value">{new Date(pendingOrderData.pickupDate).toLocaleDateString('fr-FR')}</span>
              </div>
              <div className="modal-detail-row">
                <span className="modal-label">Heure:</span>
                <span className="modal-value">{pendingOrderData.pickupTime}</span>
              </div>
            </div>

            <div className="modal-section">
              <h3>Articles commandés</h3>
              <ul className="modal-items-list">
                {pendingOrderData.items.map((item, index) => (
                  <li key={index} className="modal-item">
                    <span>{item.name}</span>
                    <span className="modal-quantity">{item.quantity} personne{item.quantity > 1 ? 's' : ''}</span>
                  </li>
                ))}
              </ul>
            </div>

            {pendingOrderData.orderComment && (
              <div className="modal-section">
                <h3>Commentaire</h3>
                <p className="modal-comment">{pendingOrderData.orderComment}</p>
              </div>
            )}

            <div className="modal-actions">
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setPendingOrderData(null);
                }}
                className="modal-cancel-button"
              >
                Annuler
              </button>
              <button
                onClick={handleConfirmOrder}
                className="modal-confirm-button"
                disabled={loading}
              >
                {loading ? 'Création...' : 'Confirmer la commande'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrderForm;
